import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Archive, Users } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import { GuestModal } from '@/components/guests/GuestModal'
import { useLabels, singular } from '@/lib/terminology'

interface GuestData {
  id: string
  name: string
  title: string
  company: string | null
  email: string | null
  status: 'active' | 'archived'
}

export function GuestsPage() {
  const { membership } = useAuth()
  const supabase = createClient()
  const navigate = useNavigate()
  const labels = useLabels()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const { data: guests = [], isLoading } = useQuery({
    queryKey: ['guests', membership?.team_id, showArchived],
    queryFn: async () => {
      if (!membership?.team_id) return []

      let query = supabase
        .from('guests')
        .select('*')
        .eq('team_id', membership.team_id)
        .order('name')

      if (!showArchived) {
        query = query.eq('status', 'active')
      }

      const { data } = await query

      return (data as GuestData[]) || []
    },
    enabled: !!membership?.team_id,
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-zinc-200 dark:bg-zinc-800 rounded mb-2" />
          <div className="h-4 w-64 bg-zinc-200 dark:bg-zinc-800 rounded mb-6" />
          <div className="bg-zinc-100 dark:bg-zinc-800/50 rounded-xl h-64" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{labels.guests}</h1>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="show-archived-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="sr-only peer"
            />
            <div className="relative w-9 h-5 bg-zinc-200 dark:bg-zinc-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-500"></div>
            <span className="text-sm text-zinc-600 dark:text-zinc-400">Show archived</span>
          </label>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors"
            data-testid="add-guest-button"
          >
            Add {singular(labels.guests)}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800">
        {guests.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-900 dark:text-zinc-100 font-medium mb-2">No {labels.guests.toLowerCase()} yet</p>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
              Add your first {singular(labels.guests).toLowerCase()} to get started
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 border border-primary-500 hover:border-primary-600 rounded-lg transition-colors"
            >
              Invite Your First {singular(labels.guests)}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {guests.map((guest) => (
              <button
                key={guest.id}
                onClick={() => navigate(`/guests/${guest.id}`)}
                className="w-full p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors flex items-center gap-4 text-left"
                data-testid={`guest-row-${guest.id}`}
              >
                <div className="relative">
                  <div className={`w-12 h-12 rounded-full bg-secondary-500 flex items-center justify-center text-white font-medium ${guest.status === 'archived' ? 'opacity-60' : ''}`}>
                    {guest.name.charAt(0)}
                  </div>
                  {guest.status === 'archived' && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-zinc-500 flex items-center justify-center">
                      <Archive className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`font-medium ${guest.status === 'archived' ? 'text-zinc-500 dark:text-zinc-400' : 'text-zinc-900 dark:text-zinc-100'}`}>{guest.name}</p>
                    {guest.status === 'archived' && (
                      <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400">
                        Archived
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-500">
                    {guest.title}
                    {guest.company ? ` at ${guest.company}` : ''}
                  </p>
                </div>
                {guest.email && <p className="text-sm text-zinc-500">{guest.email}</p>}
              </button>
            ))}
          </div>
        )}
      </div>

      <GuestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        guest={null}
      />
    </div>
  )
}
